import React, { useState, useEffect } from 'react';
import { ChevronRight, Shield, Headphones, CreditCard } from 'lucide-react';

const slides = [
  {
    id: 1,
    imgUrl: 'https://images.pexels.com/photos/3155666/pexels-photo-3155666.jpeg?auto=compress&cs=tinysrgb&w=1920',
    title: 'Откройте для себя мир',
    highlight: 'с HASAN TRAVEL',
    subtitle: 'Ваши надежные партнеры в туризме из Павлодара с 2015 года'
  },
  {
    id: 2,
    imgUrl: 'https://images.pexels.com/photos/1174732/pexels-photo-1174732.jpeg?auto=compress&cs=tinysrgb&w=1920',
    title: 'Незабываемый отдых',
    highlight: 'на лучших курортах',
    subtitle: 'Турция, Египет, ОАЭ, Таиланд, Мальдивы и другие направления'
  },
  {
    id: 3,
    imgUrl: 'https://images.pexels.com/photos/460672/pexels-photo-460672.jpeg?auto=compress&cs=tinysrgb&w=1920',
    title: 'Горящие туры',
    highlight: 'со скидкой до 40%',
    subtitle: 'Лучшие предложения каждый день — бронируйте прямо сейчас'
  }
];

const trustBadges = [
  { icon: Shield, text: '8+ лет опыта' },
  { icon: Headphones, text: 'Поддержка 24/7' },
  { icon: CreditCard, text: 'Рассрочка 0%' },
];

const Hero: React.FC = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentSlide((prev) => (prev + 1) % slides.length);
        setIsAnimating(false);
      }, 500);
    }, 6000);
    return () => clearInterval(interval);
  }, []);

  const goToSlide = (index: number) => {
    if (index === currentSlide) return;
    setIsAnimating(true);
    setTimeout(() => {
      setCurrentSlide(index);
      setIsAnimating(false);
    }, 500);
  };

  return (
    <section id="home" className="relative h-[100dvh] min-h-[650px]">
      {/* Slideshow */}
      <div className="absolute inset-0 w-full h-full overflow-hidden">
        {slides.map((slide, index) => (
          <div
            key={slide.id}
            className={`absolute inset-0 w-full h-full transition-all duration-1000 ease-in-out ${
              index === currentSlide ? 'opacity-100 scale-100' : 'opacity-0 scale-105'
            }`}
          >
            <div
              className="absolute inset-0 bg-cover bg-center"
              style={{ backgroundImage: `url(${slide.imgUrl})` }}
            >
              <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/30"></div>
            </div>
          </div>
        ))}
      </div>

      {/* Content */}
      <div className="relative h-full flex items-center pt-[100px] lg:pt-[120px]">
        <div className="container-custom">
          <div className="max-w-3xl">
            <div className={`transition-all duration-700 ${isAnimating ? 'opacity-0 translate-y-8' : 'opacity-100 translate-y-0'}`}>
              <div className="inline-block bg-secondary-300/20 backdrop-blur-sm border border-secondary-300/30 text-secondary-300 px-4 py-1.5 rounded-full text-sm font-medium mb-6">
                Туристическое агентство в Павлодаре
              </div>
              <h1 className="text-3xl md:text-4xl lg:text-6xl font-bold text-white mb-2 leading-tight">
                {slides[currentSlide].title}
              </h1>
              <h1 className="text-3xl md:text-4xl lg:text-6xl font-bold mb-4 leading-tight">
                <span className="bg-clip-text text-transparent bg-gradient-to-r from-secondary-300 to-yellow-300">
                  {slides[currentSlide].highlight}
                </span>
              </h1>
              <p className="text-base md:text-lg text-white/80 mb-6 max-w-xl">
                {slides[currentSlide].subtitle}
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 mb-8">
              <a href="#search" className="btn btn-secondary px-8 py-3.5">
                Найти тур
              </a>
              <a href="#hot-tours" className="btn border-2 border-white/30 text-white hover:bg-white/10 backdrop-blur-sm px-8 py-3.5">
                Горящие туры
              </a>
            </div>

            {/* Trust Badges */}
            <div className="flex flex-wrap gap-3">
              {trustBadges.map((badge, index) => (
                <div key={index} className="flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-lg px-4 py-2 border border-white/10">
                  <badge.icon className="w-4 h-4 text-secondary-300 flex-shrink-0" />
                  <span className="text-white/90 text-xs font-medium">{badge.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Slide Indicators */}
      <div className="absolute bottom-36 left-0 right-0 flex justify-center gap-3">
        {slides.map((_, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            className={`h-1.5 rounded-full transition-all duration-500 ${
              index === currentSlide
                ? 'bg-secondary-300 w-12'
                : 'bg-white/40 w-6 hover:bg-white/60'
            }`}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>

      {/* Scroll Indicator */}
      <a href="#search" className="absolute bottom-36 right-8 text-white/60 hover:text-white transition-colors hidden md:flex items-center gap-2 group">
        <span className="text-sm font-medium">Прокрутите вниз</span>
        <div className="w-8 h-12 rounded-full border-2 border-white/30 flex items-start justify-center pt-2">
          <div className="w-1.5 h-3 bg-white/60 rounded-full animate-bounce" />
        </div>
      </a>
    </section>
  );
};

export default Hero;
